import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

import { useAppDispatch, useAppSelector } from "store/index";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import { usePermissions } from "helpers/usePermission";

import Button from "components/buttons/Button";
import RoundBtn from "components/buttons/RoundBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";

import MainPageDragItem from "./components/MainPageDragItem";
import MainPageSortItem from "./components/MainPageSortItem";

import icon from "assets/icons/burger-icon.svg";
import noImage from "assets/images/image_not_found.webp";
import "./Widgets.scss";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import WidgetsListSkeleton from "components/global/Skeleton/WidgetsListSkeleton/WidgetsListSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

interface IPageItem {
  instanceType: string;
  instanceId?: number;
  priorityOrder: number;
  title?: string;
}

interface BurgerMenuItem {
  rowId?: string;
  value: string;
  label: string;
  img?: string;
  type: string;
  instanceId?: number | null;
  priorityOrder?: number;
  selectedLabel?: string;
}

interface MultiScopeListItem {
  id: number;
  title: string;
  isActive: boolean | number;
  image?: string | null;
  icon?: string | null;
}

interface MultiScopeOption {
  value: number;
  label: string;
  img?: string;
}

interface BurgerMenuPayloadItem {
  instanceType: string;
  instanceId?: number;
  priorityOrder: number;
}

const getItemKey = (item: BurgerMenuItem) =>
  item.rowId ?? `${item.type}:${item.value}`;
const isValidInstanceId = (value?: number | null) =>
  Number.isInteger(value) && Number(value) > 0;
const hasUnselectedMultiScope = (items: BurgerMenuItem[]) =>
  items.some(
    (item) =>
      item.value === "multiScope" && !isValidInstanceId(item.instanceId),
  );
const withPlacementOrder = (items: BurgerMenuItem[]) =>
  items.map((item, index) => ({ ...item, priorityOrder: index + 1 }));
const serializeBurgerMenuItems = (
  items: BurgerMenuItem[],
  preservePlacementOrder = false,
): BurgerMenuPayloadItem[] =>
  items.map((item, index) => {
    const payload: BurgerMenuPayloadItem = {
      instanceType: item.value,
      priorityOrder:
        preservePlacementOrder && item.priorityOrder
          ? item.priorityOrder
          : index + 1,
    };

    if (item.value === "multiScope" && isValidInstanceId(item.instanceId)) {
      payload.instanceId = Number(item.instanceId);
    }

    return payload;
  });

const BurgerMenu: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activePermissions = usePermissions("cms_widgets_burger_menu");

  const geoList = useAppSelector(selectFilteredGeo);

  const [activeGeo, setActiveGeo] = useState<string>();
  const [burgerItems, setBurgerItems] = useState<BurgerMenuItem[]>([]);
  const [pageItems, setPageItems] = useState<BurgerMenuItem[]>([]);
  const [itemsFetched, setItemsFetched] = useState(false);
  const [selectedItems, setSelectedItems] = useState<BurgerMenuItem[]>([]);
  const [selectedItemsToDelete, setSelectedItemsToDelete] = useState<
    BurgerMenuItem[]
  >([]);
  const [multiScopeOptions, setMultiScopeOptions] = useState<
    MultiScopeOption[]
  >([]);
  const [multiScopesLoading, setMultiScopesLoading] = useState(true);
  const [multiScopesError, setMultiScopesError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const revisionRef = useRef<string>();
  const requestSequence = useRef(0);
  const savingRef = useRef(false);
  const rowSequence = useRef(0);
  const geoRef = useRef(activeGeo);
  geoRef.current = activeGeo;
  const [hasConflict, setHasConflict] = useState(false);
  const permissions = usePermissionHelper();
  const newRowId = () => `burger-row-${++rowSequence.current}`;
  const unusedScopes = multiScopeOptions.filter(
    (option) =>
      !pageItems.some(
        (item) =>
          item.value === "multiScope" && item.instanceId === option.value,
      ),
  );
  const emptyScopeCount = pageItems.filter(
    (item) =>
      item.value === "multiScope" && !isValidInstanceId(item.instanceId),
  ).length;
  const canClone =
    !multiScopesLoading &&
    !multiScopesError &&
    unusedScopes.length > emptyScopeCount;

  const restoreItems = (items: IPageItem[] = []) =>
    items.map((el) => ({
      rowId: newRowId(),
      value: el.instanceType,
      type: el.instanceType,
      label: t(el.instanceType),
      img: icon,
      instanceId:
        el.instanceType === "multiScope" &&
        isValidInstanceId(Number(el.instanceId))
          ? Number(el.instanceId)
          : undefined,
      priorityOrder: el.priorityOrder,
      selectedLabel: el.instanceType === "multiScope" ? el.title : undefined,
    }));

  useEffect(() => {
    if (!activePermissions?.includes("read") && activePermissions !== null) {
      navigate("/dashboard");
    }
  }, []);

  useEffect(() => {
    setActiveGeo(geoList[0]);
  }, [geoList]);

  const getPageItems = async () => {
    const requestedGeo = activeGeo;
    const requestId = ++requestSequence.current;
    setItemsFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.burgerMenuList}?geo=${requestedGeo}`,
      );
      if (
        geoRef.current !== requestedGeo ||
        requestId !== requestSequence.current
      )
        return;
      revisionRef.current = result.data.revision;
      setPageItems(restoreItems(result.data.items));
      setSelectedItems([]);
      setSelectedItemsToDelete([]);
      setHasConflict(false);
    } catch {
      if (
        geoRef.current !== requestedGeo ||
        requestId !== requestSequence.current
      )
        return;
      revisionRef.current = undefined;
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityFetchError"),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      if (
        geoRef.current === requestedGeo &&
        requestId === requestSequence.current
      )
        setItemsFetched(true);
    }
  };

  useEffect(() => {
    revisionRef.current = undefined;
    setSelectedItems([]);
    setSelectedItemsToDelete([]);
    setHasConflict(false);
    if (activeGeo) getPageItems();
  }, [activeGeo]);

  const getItems = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.burgerMenuItems}?limit=500&offset=0`,
      );
      setBurgerItems(
        result.data.items?.map((el: string) => ({
          value: el,
          label: t(el),
          img: icon,
          type: el,
        })) ?? [],
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getMultiScopes = async () => {
    setMultiScopesLoading(true);
    setMultiScopesError(false);

    try {
      const items: MultiScopeListItem[] = [];
      let offset = 0;
      const limit = 30;
      while (true) {
        const result = await instance.get(apiRoutes.multiScopeList, {
          params: { limit, offset, isActive: true },
        });
        const payload = result.data?.data ?? result.data;
        const batch: MultiScopeListItem[] = payload?.items ?? [];
        items.push(...batch);
        offset += batch.length;
        if (
          !batch.length ||
          (payload?.totalItems != null
            ? offset >= payload.totalItems
            : batch.length < limit)
        )
          break;
      }

      setMultiScopeOptions(
        items
          .filter((item) => item.isActive === true || item.isActive === 1)
          .map((item) => ({
            value: item.id,
            label: item.title,
            img: item.image ?? item.icon ?? noImage,
          })),
      );
    } catch (error) {
      console.error(error);
      setMultiScopeOptions([]);
      setMultiScopesError(true);
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityFetchError"),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      setMultiScopesLoading(false);
    }
  };

  useEffect(() => {
    getItems();
    getMultiScopes();
  }, []);

  const handleUpdateItems = async (
    items: BurgerMenuItem[],
    preservePlacementOrder = false,
  ) => {
    if (
      savingRef.current ||
      hasConflict ||
      !revisionRef.current ||
      hasUnselectedMultiScope(items)
    )
      return false;
    savingRef.current = true;
    setIsSaving(true);
    const requestedGeo = activeGeo;
    try {
      const result = await instance.post(apiRoutes.burgerMenuUpdate, {
        geo: requestedGeo,
        expectedRevision: revisionRef.current,
        items: serializeBurgerMenuItems(items, preservePlacementOrder),
      });
      if (geoRef.current !== requestedGeo) return true;
      revisionRef.current = result.data.revision;
      setPageItems(restoreItems(result.data.items));
      setSelectedItemsToDelete([]);
      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsUpdated"),
          type: NotificationTypes.success,
        }),
      );
      return true;
    } catch (error: any) {
      if (geoRef.current !== requestedGeo) return false;
      const conflict = error?.status === 409 || error?.response?.status === 409;
      if (conflict) setHasConflict(true);
      dispatch(
        showNotification({
          title: t("error"),
          message: t(conflict ? "burgerMenuConflict" : "itemsUpdateError"),
          type: NotificationTypes.error,
        }),
      );
      return false;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const moveItem = (dragIndex: number, hoverIndex: number) => {
    if (savingRef.current || hasConflict) return;
    const items = [...pageItems];
    const [reorderedItem] = items.splice(dragIndex, 1);
    items.splice(hoverIndex, 0, reorderedItem);
    const orderedItems = withPlacementOrder(items);
    setPageItems(orderedItems);
    handleUpdateItems(orderedItems, true);
  };

  return (
    <div className="main-page-wrapper burger-page-wrapper">
      <div className="main-page-content">
        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-widget--tabs">
              <div className="game-modal-tabs small">
                {geoList.map((el) => (
                  <div
                    className={`tab-item ${activeGeo === el ? "active" : ""}`}
                    key={el}
                    onClick={() => {
                      if (savingRef.current || activeGeo === el) return;
                      setActiveGeo(el);
                      setPageItems([]);
                    }}
                  >
                    {el.toLowerCase() === "ww" ? (
                      <span className="mr-5">🌎</span>
                    ) : (
                      <img
                        src={`https://flagcdn.com/w40/${el.toLowerCase()}.png`}
                        alt=""
                      />
                    )}
                    {t(el)}
                  </div>
                ))}
              </div>
            </div>
            {hasConflict && (
              <div role="alert">
                <p>{t("burgerMenuConflict")}</p>
                <button type="button" onClick={getPageItems}>
                  {t("burgerMenuReload")}
                </button>
              </div>
            )}
            {itemsFetched ? (
              <div className="main-page-widget--list">
                {pageItems.map((el, index) => (
                  <MainPageSortItem
                    isBurger
                    handleClick={() => {
                      if (!permissions?.delete || isSaving || hasConflict)
                        return;

                      if (
                        selectedItemsToDelete.some(
                          (item) => getItemKey(item) === getItemKey(el),
                        )
                      ) {
                        setSelectedItemsToDelete(
                          selectedItemsToDelete.filter(
                            (item) => getItemKey(item) !== getItemKey(el),
                          ),
                        );
                      } else {
                        setSelectedItemsToDelete([
                          ...selectedItemsToDelete,
                          el,
                        ]);
                      }
                    }}
                    isSelected={selectedItemsToDelete.some(
                      (item) => getItemKey(item) === getItemKey(el),
                    )}
                    key={getItemKey(el)}
                    item={el}
                    index={index}
                    moveItem={moveItem}
                    isDraggable={
                      permissions?.update && !isSaving && !hasConflict
                    }
                    customContent={
                      el.value === "multiScope" ? (
                        <div
                          className="multi-scope-dropdown-wrapper"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {permissions?.create && (
                            <Button
                              isSmall
                              isSecondary
                              txt={
                                <span aria-label={t("burgerMenuClone")}>
                                  {t("copy")}
                                </span>
                              }
                              disabled={!canClone || isSaving || hasConflict}
                              handleClick={() => {
                                if (
                                  savingRef.current ||
                                  hasConflict ||
                                  !canClone
                                )
                                  return;
                                const cloned = {
                                  ...el,
                                  rowId: newRowId(),
                                  instanceId: undefined,
                                  selectedLabel: undefined,
                                };
                                const items = [...pageItems];
                                items.splice(index + 1, 0, cloned);
                                setPageItems(withPlacementOrder(items));
                              }}
                            />
                          )}
                          <DefaultDropdown
                            value={el.instanceId ?? undefined}
                            selectedLabel={
                              el.selectedLabel ??
                              (isValidInstanceId(el.instanceId)
                                ? `${t("multiScope")} #${el.instanceId} (${t("inActive")})`
                                : undefined)
                            }
                            placeholder={
                              multiScopesError
                                ? t("entityFetchError")
                                : t("select")
                            }
                            options={multiScopeOptions.filter(
                              (option) =>
                                option.value === el.instanceId ||
                                !pageItems.some(
                                  (other) =>
                                    other.rowId !== el.rowId &&
                                    other.value === "multiScope" &&
                                    other.instanceId === option.value,
                                ),
                            )}
                            onSelect={(value) => {
                              if (savingRef.current || hasConflict) return;
                              const instanceId = Number(value);
                              const selectedOption = multiScopeOptions.find(
                                (option) => option.value === instanceId,
                              );
                              const updated = pageItems.map(
                                (item, itemIndex) =>
                                  itemIndex === index
                                    ? {
                                        ...item,
                                        instanceId,
                                        selectedLabel: selectedOption?.label,
                                      }
                                    : item,
                              );
                              setPageItems(updated);
                              handleUpdateItems(updated, true);
                            }}
                            disabled={
                              !permissions?.update || isSaving || hasConflict
                            }
                            loading={multiScopesLoading}
                            emptyText={t("nothingFound")}
                            hasError={
                              !isValidInstanceId(el.instanceId) ||
                              (!multiScopesLoading &&
                                !multiScopesError &&
                                !multiScopeOptions.some(
                                  (option) =>
                                    option.value === Number(el.instanceId),
                                ))
                            }
                            isEllipsis
                            isBot
                            useFixedMenu
                          />
                        </div>
                      ) : undefined
                    }
                  />
                ))}
              </div>
            ) : (
              <WidgetsListSkeleton />
            )}
            <div className="main-page-btns">
              {permissions?.create && (
                <RoundBtn
                  handleClick={() => {
                    if (savingRef.current || hasConflict) return;
                    const newArray = withPlacementOrder([
                      ...pageItems,
                      ...selectedItems.map((item) => ({
                        ...item,
                        rowId: newRowId(),
                      })),
                    ]);
                    setSelectedItems([]);
                    setPageItems(newArray);
                    handleUpdateItems(newArray, true);
                  }}
                  txt="+"
                  disabled={
                    selectedItems.length === 0 ||
                    isSaving ||
                    hasConflict ||
                    (selectedItems.some(
                      (item) => item.value === "multiScope",
                    ) &&
                      !canClone)
                  }
                />
              )}
              {permissions?.delete && (
                <RoundBtn
                  handleClick={() => {
                    if (savingRef.current || hasConflict) return;
                    const newArray = pageItems.filter(
                      (el) =>
                        !selectedItemsToDelete.some(
                          (item) => getItemKey(item) === getItemKey(el),
                        ),
                    );
                    const orderedItems = withPlacementOrder(newArray);
                    handleUpdateItems(orderedItems, true);
                    setSelectedItemsToDelete([]);
                    setPageItems(orderedItems);
                  }}
                  txt="-"
                  disabled={
                    selectedItemsToDelete.length === 0 ||
                    isSaving ||
                    hasConflict
                  }
                />
              )}
            </div>
          </div>
        </div>
        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-title">{t("menuItems")}</div>
            <div className="main-page-list-content">
              {itemsFetched ? (
                <>
                  {burgerItems
                    .filter(
                      (item) =>
                        !pageItems.some(
                          (pageItem) => pageItem.value === item.value,
                        ),
                    )
                    .map((el: any) => (
                      <MainPageDragItem
                        isBurger
                        handleClick={() => {
                          if (!permissions?.create || isSaving || hasConflict)
                            return;
                          if (
                            selectedItems.some(
                              (item) => getItemKey(item) === getItemKey(el),
                            )
                          ) {
                            setSelectedItems(
                              selectedItems.filter(
                                (item) => getItemKey(item) !== getItemKey(el),
                              ),
                            );
                          } else {
                            setSelectedItems([...selectedItems, el]);
                          }
                        }}
                        isSelected={selectedItems.some(
                          (item) => getItemKey(item) === getItemKey(el),
                        )}
                        key={getItemKey(el)}
                        item={el}
                        type={el.type}
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BurgerMenu;
